/*
 * Copyright (c) 2017 Datarista, Inc.
 */

import React from 'react';
import lsStore from 'store';
import _ from 'lodash';
import moment from 'moment';
import {
  DEFAULT_TIME_POSTFIX,
  PORTAL_USER,
  INSTALL_ADMIN,
  SEARCH_DATE_DISPLAY_FORMAT,
  SEARCH_DATE_FORMAT,
  RECORD_LEVEL_TYPE_COMPANY,
  RECORD_LEVEL_TYPE_CONTACT,
  RECORD_LEVEL_TYPE_ITEM,
  RECORD_LEVEL_TYPE_LEAD,
  SPECIAL_PATH_ACCESS_LEVELS,
  SUPER_ADMIN,
} from '../config/app_config';

/*
  IMPORTANT:
  storeWithExpiration has been copied to the search_frame application.
  if this changes, that should probably change too.
 */
export const storeWithExpiration = {
  set: (key, val, exp) => {
    lsStore.set(key, { val, exp, time: new Date().getTime() });
  },
  get: key => {
    const info = lsStore.get(key);
    if (!info) {
      return null;
    }
    if (new Date().getTime() - info.time > info.exp) {
      return null;
    }
    return info.val;
  },
};

export const toIdString = str => str.replace(/\s+/g, '-');

export const formatDollar = value => parseFloat(Math.round(value * 100) / 100).toFixed(2);

export const addCommas = num => (num ? num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',') : '');

export const getCostPerItem = (numItems, bucketPriceArray, isCRMTable) => {
  const costPerItemObj = bucketPriceArray.filter((bucket, index) => {
    let isPriceBucket = false;
    if (isCRMTable) {
      isPriceBucket = numItems >= bucket.min && numItems <= bucket.max;
    } else if (index === 0) {
      isPriceBucket = numItems >= bucket.min && numItems <= bucket.max * 1000;
    } else {
      isPriceBucket = numItems >= bucket.min * 1000 && numItems <= bucket.max * 1000;
    }

    return isPriceBucket;
  })[0];

  let costPerItem = bucketPriceArray[bucketPriceArray.length - 1].floor;
  if (costPerItemObj) {
    costPerItem = costPerItemObj.floor;
  }

  return costPerItem;
};

export const getOrderTotalCost = (numItems, bucketPriceArray, isCRMTable) => {
  const itemCost = getCostPerItem(numItems, bucketPriceArray, isCRMTable);
  const totalCost = isCRMTable ? numItems * itemCost : Math.ceil(numItems / 1000) * itemCost;
  const formattedTotal = addCommas(formatDollar(totalCost));

  return formattedTotal;
};

// Check if Customer Portal User
export const checkCPUser = userTypeId => userTypeId === PORTAL_USER;

// Check if Customer Portal User is Admin on any of the instances
export const checkCPAdmin = installations => {
  let isAdmin = false;
  installations.valueSeq().forEach(install => {
    if (install.installationUser.get('permissions') === INSTALL_ADMIN) {
      isAdmin = true;
    }
  });

  return isAdmin;
};

// Check if Customer Portal User is admin of specific instance/installation
export const checkInstallAdmin = permLevel => permLevel === INSTALL_ADMIN;

// common jsx
export const renderSR = item => <span className="sr-only">{item}</span>;

export const renderDisabledStatus = () => (
  <span>
    <i className="fa fa-ellipsis-h" style={{ color: 'rgb(231,171,0)' }} />
    &nbsp;
    <weak>Update Pending</weak>
  </span>
);

export const renderEnabledStatus = () => (
  <span>
    <i className="fa fa-check-circle" style={{ color: 'rgb(0,199,123)' }} />
    &nbsp;
    <weak>Update is Live</weak>
  </span>
);

export const setUserToEdit = (users, loggedInUser, propsId) => {
  if (loggedInUser.id === propsId) {
    return loggedInUser;
  }
  return _.find(users._tail.array, { id: propsId });
};

// Originally this was similar to the search frame formatParamData, but it's been changed a bit
export const formatParamData = paramData => {
  const paramToAdd = {
    name: '',
    displayValue: '',
  };
  const paramArray = paramData.fieldValues;

  // Add the fieldName, Add displayName once available
  paramToAdd.name = paramData.fieldName;
  paramToAdd.displayName = paramData.fieldName.replace(/^c\./i, '');

  // If values are from a  code array, format them
  if (paramArray.length > 1) {
    const descriptionText = paramArray.map(param => param.value).join(', ');
    paramToAdd.displayValue = descriptionText;
  } else {
    const paramVal = paramArray[0];
    // Else if a range, format it
    if (paramVal.min || paramVal.max) {
      // If a date range, format it (three ways to format)
      // TODO: We're using DEFAULT_TIME_POSTFIX to verify that it's a date.
      // This isn't ideal (as removing the format could break this), but it works for now.
      // Should revisit later.
      if (paramVal.min && paramVal.max && paramVal.min.indexOf(DEFAULT_TIME_POSTFIX) !== -1) {
        paramToAdd.displayValue = `${moment(paramVal.min, SEARCH_DATE_FORMAT).format(
          SEARCH_DATE_DISPLAY_FORMAT
        )} - ${moment(paramVal.max, SEARCH_DATE_FORMAT).format(SEARCH_DATE_DISPLAY_FORMAT)}`;
      } else if (paramVal.min && paramVal.min.indexOf(DEFAULT_TIME_POSTFIX) !== -1) {
        paramToAdd.displayValue = `since: ${moment(paramVal.min, SEARCH_DATE_FORMAT).format(
          SEARCH_DATE_DISPLAY_FORMAT
        )}`;
      } else if (paramVal.max && paramVal.max.indexOf(DEFAULT_TIME_POSTFIX) !== -1) {
        paramToAdd.displayValue = `on or before: ${moment(paramVal.max, SEARCH_DATE_FORMAT).format(
          SEARCH_DATE_DISPLAY_FORMAT
        )}`;
      } else if (paramVal.min && paramVal.max) {
        // If a number, set up range format
        paramToAdd.displayValue = `${paramVal.min} - ${paramVal.max}`;
      } else if (paramVal.min) {
        paramToAdd.displayValue = `${paramVal.min} or more`;
      } else if (paramVal.max) {
        paramToAdd.displayValue = `${paramVal.max} or less`;
      }
    } else if (paramVal.value.indexOf(DEFAULT_TIME_POSTFIX) !== -1) {
      // If single date, format it
      paramToAdd.displayValue = moment(paramVal, SEARCH_DATE_FORMAT).format(
        SEARCH_DATE_DISPLAY_FORMAT
      );
    } else {
      // Otherwise just use the value as a display value
      paramToAdd.displayValue = paramVal.value;
    }
  }

  return paramToAdd;
};

export const checkUserPermissions = (accessLevel, path) => {
  let accessible = true;
  const basePath = path[0];
  const formattedPath = path;
  const pathStr = formattedPath.join('/');
  if (basePath === 'superadmin' && accessLevel !== SUPER_ADMIN) {
    accessible = false;
  } else {
    let pathRegEx = '';
    let pathKey = '';
    const needsPermission = Object.keys(SPECIAL_PATH_ACCESS_LEVELS).some(key => {
      pathKey = key;
      pathRegEx = new RegExp(key, 'i');
      return pathRegEx.test(pathStr);
    });
    if (needsPermission) {
      accessible = SPECIAL_PATH_ACCESS_LEVELS[pathKey].indexOf(accessLevel) !== -1;
    }
  }
  return accessible;
};

export const getOrderStatus = endDate => {
  const currentDateStamp = new Date().getTime();
  const orderEndDateStamp = new Date(endDate).getTime();
  const orderEndDate = new Date(endDate);
  const oneMonthBeforeEndDate = orderEndDate.setMonth(orderEndDate.getMonth() - 1);
  if (currentDateStamp > orderEndDateStamp) {
    return { key: 2, text: 'Expired', color: 'danger', group: 'expired' };
  } else if (currentDateStamp <= orderEndDateStamp && oneMonthBeforeEndDate < currentDateStamp) {
    return { key: 1, text: 'Expiring', color: 'warning', group: 'active' };
  }
  return { key: 0, text: 'Active', color: 'success', group: 'active' };
};

export const compare = (a, b) => {
  if (a.status > b.status) {
    return -1;
  }
  if (a.status < b.status) {
    return 1;
  }
  return 0;
};

// Render display text for record level types
export const renderRLTLabel = recordLevelType => {
  let label = '';

  switch (recordLevelType) {
    case RECORD_LEVEL_TYPE_COMPANY:
      label = 'Company';
      break;
    case RECORD_LEVEL_TYPE_CONTACT:
      label = 'Contact';
      break;
    case RECORD_LEVEL_TYPE_ITEM:
      label = 'Item';
      break;
    case RECORD_LEVEL_TYPE_LEAD:
      label = 'Lead';
      break;
    default:
      label = '';
      break;
  }

  return label;
};
